import React, { useMemo } from 'lib/react';
import {
  EVWallsIcon, EVWholeHomeIcon, EVWbSunnyOutlinedIcon, EVRoofIcon, EVOtherIcon, EVGrid,
} from 'lib/components';
import { useSelector } from 'lib/redux';
import {
  LABELS_FOR_PRODUCT_AVAILABILITY, MULTIFAMILY, PRODUCT_IDS,
} from 'constants/AppConstants';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC } from 'constants/FeatureFlag.constants';
import { featureFlagSelector } from 'utils/appSelectors.utils';
import LinearProgressWithLabel from 'commonComponents/progress-bar/LinearProgressWithLabel.component';
import * as productCategories from './ProductList.constants';
import PrimaryProductTemplate from './PrimaryProduct.template';
import OtherProductsTemplate from './OtherProducts.template';
import { groupProductsIntoCategories, getAlertMessageForQSMF, filterNotSetProducts } from './ProductList.util';
import ProductStyles from './ProductStyles';

function ProductList() {
  const {
    userProducts, propertyType, productAvailabilityLoading, solarProductsAvailability,
    loading: { userProducts: userProductsLoading, initParameters: initParametersLoading },
  } = useSelector(({ order, userData }) => ({
    ...order,
    userProducts: userData.userProducts,
    loading: userData.loading,
  }));
  const isESXEnabled = useSelector(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));
  const classes = ProductStyles();
  const products = useMemo(() => {
    if (!userProductsLoading && userProducts[propertyType]) {
      // #genai - Safely inspect the optional NotSet product collection.
      const notSetProducts = (propertyType !== MULTIFAMILY && userProducts.NotSet?.PrimaryProducts)
        ? userProducts.NotSet.PrimaryProducts.filter((p) => filterNotSetProducts(p, propertyType, isESXEnabled))
        : [];
      const propertyTypeProducts = [
        ...userProducts[propertyType].PrimaryProducts,
        ...notSetProducts,
      ];

      const { solar, ...productCategoriesList } = groupProductsIntoCategories(propertyTypeProducts)(false, propertyType);
      return {
        ...productCategoriesList,
        solar: solar?.filter((product) => solarProductsAvailability[product.ProductID]
          || [PRODUCT_IDS.EagleViewInformEssentialsPlus, PRODUCT_IDS.EagleViewInformEssentialsPlusforCommercial].includes(product.ProductID)),
      };
    }
    return null;
  }, [userProductsLoading, propertyType, userProducts, solarProductsAvailability, isESXEnabled]);

  if (productAvailabilityLoading || initParametersLoading || userProductsLoading) {
    return <LinearProgressWithLabel labels={LABELS_FOR_PRODUCT_AVAILABILITY} />;
  }
  return products && (
    <>
      <EVGrid id="order-product-list">
        <PrimaryProductTemplate
          name={productCategories.WHOLE_HOME_PRODUCTS}
          products={products.wholeHome || []}
          icon={<EVWholeHomeIcon className={classes.iconStyle} />}
          selectedProductKey="selectedPrimaryProduct"
          selectedDeliveryProductKey="selectedPrimaryDeliveryProduct"
          selectComponentType="radio"
        />
        <PrimaryProductTemplate
          name={productCategories.ROOF_PRODUCTS}
          products={products.roof || []}
          icon={<EVRoofIcon className={classes.iconStyle} />}
          selectedProductKey="selectedPrimaryProduct"
          selectedDeliveryProductKey="selectedPrimaryDeliveryProduct"
          selectComponentType="radio"
        />
        <PrimaryProductTemplate
          name={productCategories.SOLAR_PRODUCTS}
          products={products.solar || []}
          icon={<EVWbSunnyOutlinedIcon htmlColor="#3E66F1" className={classes.iconStyle} />}
          selectedProductKey="selectedPrimaryProduct"
          selectedDeliveryProductKey="selectedPrimaryDeliveryProduct"
          selectComponentType="radio"
        />
        <PrimaryProductTemplate
          name={productCategories.WALLS_PRODUCTS}
          products={products.walls || []}
          icon={<EVWallsIcon className={classes.iconStyle} />}
          selectedProductKey="selectedWallsProduct"
          selectedDeliveryProductKey="selectedWallsDeliveryProduct"
          selectComponentType="checkbox"
        />
        <PrimaryProductTemplate
          name={productCategories.DATA_PRODUCTS}
          products={products.data || []}
          icon={<EVOtherIcon className={classes.iconStyle} />}
          selectedProductKey="selectedPrimaryProduct"
          selectedDeliveryProductKey="selectedDataDeliveryProduct"
          selectComponentType="radio"
        />
        <OtherProductsTemplate
          name={productCategories.OTHER_PRODUCTS}
          products={products.others || []}
          icon={<EVOtherIcon className={classes.iconStyle} />}
        />
      </EVGrid>
      {products !== null && products.roof?.length === 1 && products.roof[0].ProductID === 55 && (
        <EVGrid id="test-qsmf-container" className={classes.qsmfContainer}>
          {getAlertMessageForQSMF()}
        </EVGrid>
      )}
    </>
  );
}

export default ProductList;
